import { useCallback, useState } from "react"

import { Guid } from "@/types"
import { IArticleLevelsProps } from "./ArticleLevels"
import ArticleService from "services/ArticleService"
import toastMessages from "stores/toastMessages"
import { uniq } from "lodash"
import ArticleAsset from "models/ArticleAsset"

interface IUseArticleLevelsParams {
    currentArticleId?: Guid
}

export interface IUseArticleLevels extends IArticleLevelsProps {
    highLevelArticleNumber: Guid | null
    lowLevelArticleNumbers: Guid[]
    initArticleLevels: (params: { highLevelArticleNumber: Guid | null, lowLevelArticleNumbers: Guid[] }) => void
}

export default function useArticleLevels({
    currentArticleId,
}: IUseArticleLevelsParams = {}): IUseArticleLevels {
    const [highLevelArticleNumber, setHighLevelArticleNumber] = useState<Guid | null>(null)
    const [lowLevelArticleNumbers, setLowLevelArticleNumbers] = useState<Guid[]>([])
    const [highLevelArticle, setHighLevelArticle] = useState<ArticleAsset | null>(null)
    const [lowLevelArticles, setLowLevelArticles] = useState<ArticleAsset[]>([])
    const [isFethingSelectedHighLevelArticle, setIsFethingSelectedHighLevelArticle] = useState(false)
    const [isFethingSelectedLowLevelArticle, setIsFethingSelectedLowLevelArticle] = useState(false)

    const initArticleLevels = useCallback(async (initParams: {
        highLevelArticleNumber: Guid | null,
        lowLevelArticleNumbers: Guid[]
    }) => {
        if (initParams.highLevelArticleNumber) {
            setIsFethingSelectedHighLevelArticle(true)
        } else {
            setHighLevelArticleNumber(null)
            setHighLevelArticle(null)
        }
        if (initParams.lowLevelArticleNumbers.length) {
            setIsFethingSelectedLowLevelArticle(true)
        } else {
            setLowLevelArticleNumbers([])
            setLowLevelArticles([])
        }

        if (!initParams.highLevelArticleNumber && !initParams.lowLevelArticleNumbers.length) {
            return
        }

        const articleNumbersToFetch = uniq([
            ...(initParams.highLevelArticleNumber ? [initParams.highLevelArticleNumber] : []),
            ...initParams.lowLevelArticleNumbers,
        ])

        const resp = await ArticleService.getArticleAssets({
            articleNumbers: articleNumbersToFetch,
            pageSize: articleNumbersToFetch.length,
            isOnlyForHolder: false,
        })

        if (!resp) {
            console.error("Failed to fetch article levels")
            toastMessages.showSystemError()
            setIsFethingSelectedHighLevelArticle(false)
            setIsFethingSelectedHighLevelArticle(false)
            return
        }

        const newHighLevelArticle = resp.list.find((a) => a.articleNo === initParams.highLevelArticleNumber) || null
        if (newHighLevelArticle) {
            setHighLevelArticleNumber(newHighLevelArticle.articleNo)
            setHighLevelArticle(newHighLevelArticle)
        } else {
            setHighLevelArticleNumber(null)
            setHighLevelArticle(null)
        }

        const newLowLevelArticles = resp.list.filter((a) => initParams.lowLevelArticleNumbers.includes(a.articleNo))
        if (newLowLevelArticles.length) {
            setLowLevelArticleNumbers(newLowLevelArticles.map((a) => a.articleNo))
            setLowLevelArticles(newLowLevelArticles)
        } else {
            setLowLevelArticleNumbers([])
            setLowLevelArticles([])
        }

        setIsFethingSelectedHighLevelArticle(false)
        setIsFethingSelectedLowLevelArticle(false)
    }, [])

    const addHighLevelArticle = useCallback(async (articleNumber: string) => {
        setIsFethingSelectedHighLevelArticle(true)
        try {
            const resp = await ArticleService.getArticleAssets({
                articleNumbers: [articleNumber],
                isOnlyForHolder: false,
            })
            const article = resp?.list[0]
            if (article) {
                setHighLevelArticle(article)
                setHighLevelArticleNumber(articleNumber)
            }
        } catch(e) {
            toastMessages.showSystemError()
        }
        setIsFethingSelectedHighLevelArticle(false)
    }, [])

    const removeHighLevelArticle = useCallback(() => {
        setHighLevelArticleNumber(null)
        setHighLevelArticle(null)
    }, [])

    const addLowLevelArticle = useCallback(async (articleNumber: string) => {
        setIsFethingSelectedLowLevelArticle(true)
        try {
            const resp = await ArticleService.getArticleAssets({
                articleNumbers: [articleNumber],
                isOnlyForHolder: false,
            })
            const article = resp?.list[0]
            if (article) {
                setLowLevelArticles((prev) => [...prev, article])
                setLowLevelArticleNumbers((prev) => [...prev, articleNumber])
            }
        } catch(e) {
            toastMessages.showSystemError()
        }
        setIsFethingSelectedLowLevelArticle(false)
    }, [])

    const removeLowLevelArticle = useCallback((articleNumber: string) => {
        setLowLevelArticleNumbers((prev) => prev.filter((n) => n !== articleNumber))
        setLowLevelArticles((prev) => prev.filter((a) => a.articleNo !== articleNumber))
    }, [])

    return {
        currentArticleId,
        initArticleLevels,

        highLevelArticleNumber,
        highLevelArticle,
        isFethingSelectedHighLevelArticle,

        lowLevelArticleNumbers,
        lowLevelArticles,
        isFethingSelectedLowLevelArticle,

        addHighLevelArticle,
        addLowLevelArticle,
        removeHighLevelArticle,
        removeLowLevelArticle,
    }
}
